import {ShopContainer} from "../ShopContainer";
import {parseCents} from "nate-react-api-helpers";
import React, {useState} from "react";
import {TableCellTextPopper} from "../../../../misc/scroller/TableEditCell";
import {useUser} from "../../../../misc/Permission";
import {MiscProduct} from "../../../../api/MiscProducts";
import {Frames, PriceLine2} from "./Frames";
import {Doors} from "./Doors";
import {Hardware} from "./Hardware";
import {QuotePricingSwitcher} from "../../quote/pricing/QuotePricingSwitcher";
import {useTabQueryParam} from "../../../../misc/userpref/useTabQueryParam";
import {DefaultTabControls} from "../../../../misc/userpref/useDefaultTab";
import {Div10} from "./Div10";
import {Glazing} from "./Glazing";
import {Summary} from "./Summary";
import {CostHelperProvider} from "./CostHelperContext";
import {CostHelper} from "./CostHelper";
import {PriceSectionNotes} from "./PriceSectionNotes";
import {QuotePriceLine} from "../../../../api/QuotePricing";
import {PricingTabs} from "./PricingTabs";
import {projectTablePrefName} from "../TablePrefName";
import {MiscPricing} from "./MiscPricing";
import {ProjectPageTitle} from "../ProjectPageTitle";
import {Riders} from "./Riders";
import {PriceRequestSubmittal} from "./PriceRequestSubmittal";
import {CheckboxExtProvider} from "../../manage/sov/CheckboxExtProvider";

export function priceTableName(project: number, tab: string, tab2: string = "default") {
    if(tab === "div10") {
        tab2 = "div10/" + tab2;
    }

    return projectTablePrefName("project.pricing." + tab + "." + tab2, project);
}

export function Pricing() {
    const u = useUser();
    const [tab, setTab, dt] = useTabQueryParam({
        page: "shopdrawing.pricing", urlKey: "tab", fallback: "frame-hm",
        allowed: ["frame-hm", "frame-anchor", "door-hm", "door-wd", "hardware", "glazing", "div10", "rider", "misc"],
    });

    if(!u) return null;

    return <ShopContainer
            name={<QuotePricingSwitcher />}
            actions={<PriceRequestSubmittal tab={tab} />}
        >
        <ProjectPageTitle title="Pricing" />
        <PricingGeneric
            tab={tab}
            onTabChange={setTab}
            defaultTab={dt}
            hideSummary
        />
    </ShopContainer>
}

export const CheckboxPriceSummaryName = "summary";
export const CheckboxPriceFrameHmName = "frame-hm";
export const CheckboxPriceFrameAnchorName = "frame-anchor";
export const CheckboxPriceDoorHmName = "door-hm";
export const CheckboxPriceDoorWdName = "door-wd";
export const CheckboxPriceHardwareName = "hardware";
export const CheckboxPriceMiscName = "misc";
export const CheckboxPriceRiderName = "rider";
export const CheckboxPriceDiv10Name = "div-10";
export const CheckboxPriceGlazingName = "glazing";

export function PricingGeneric(props: {
    hideTabs?: boolean;
    hideSummary?: boolean;
    tab: string;
    onTabChange(value: string): void;
    defaultTab?: DefaultTabControls;
    filterByChangeset?: boolean;
    sov?: number;
}) {

    return (
        <>
            <CostHelperProvider>
                {props.hideTabs ? null : <PricingTabs {...props}
                                                      refunds={props.filterByChangeset ? false : undefined} />}

                <div style={{
                    flex: 1, display: "flex",
                    flexDirection: "column",
                    overflow: "auto"
                }}>
                    {props.tab === "frame-hm" ?
                        <CheckboxExtProvider name={CheckboxPriceFrameHmName}>
                            <Frames filterByChangeset={props.filterByChangeset} sov={props.sov} />
                        </CheckboxExtProvider>:
                        props.tab === "frame-anchor" ? <CheckboxExtProvider name={CheckboxPriceFrameAnchorName}>
                            <Hardware filterByChangeset={props.filterByChangeset} kind="frame-anchor" sov={props.sov} />
                        </CheckboxExtProvider> :
                        props.tab === "door-hm" ? <CheckboxExtProvider name={CheckboxPriceDoorHmName}>
                            <Doors filterByChangeset={props.filterByChangeset} kind={"hm"} sov={props.sov} />
                        </CheckboxExtProvider> :
                        props.tab === "door-wd" ?
                            <CheckboxExtProvider name={CheckboxPriceDoorWdName}>
                                <Doors filterByChangeset={props.filterByChangeset} kind={"wd"} sov={props.sov} />
                            </CheckboxExtProvider> :
                        props.tab === "hardware" ?
                            <CheckboxExtProvider name={CheckboxPriceHardwareName}>
                                <Hardware filterByChangeset={props.filterByChangeset} kind="hardware" sov={props.sov} />
                            </CheckboxExtProvider> :
                        props.tab === "summary" && !props.hideSummary ?
                            <CheckboxExtProvider name={CheckboxPriceSummaryName}>
                                <Summary sov={props.sov} />
                            </CheckboxExtProvider> :
                        props.tab === "misc" ?
                            <CheckboxExtProvider name={CheckboxPriceMiscName}>
                                <MiscPricing sov={props.sov} />
                            </CheckboxExtProvider>:
                        props.tab === "glazing" ?
                            <CheckboxExtProvider name={CheckboxPriceGlazingName}>
                                <Glazing filterByChangeset={props.filterByChangeset} sov={props.sov} />
                            </CheckboxExtProvider> :
                        props.tab === "rider" ?
                            <CheckboxExtProvider name={CheckboxPriceRiderName}>
                                <Riders sov={props.sov} />
                            </CheckboxExtProvider> :
                            <CheckboxExtProvider name={CheckboxPriceDiv10Name}>
                                <Div10 filterByChangeset={props.filterByChangeset} sov={props.sov} />
                            </CheckboxExtProvider>}

                    {props.filterByChangeset || props.sov ? null : <>
                        <PriceSectionNotes key={props.tab} tab={props.tab} subTab={""} />
                    </>}
                </div>
                <CostHelper />
            </CostHelperProvider>
        </>
    )
}

export function showNumber(name: string, value?: number) {
    if(!value) return name;

    return <div style={{display: "flex", alignItems: "center", flexWrap: "nowrap"}}>
        {name}
        <span style={{fontSize: "0.75rem"}}>&nbsp;({value})</span>
    </div>
}

export function byFixedOrder(list: string[]): (a: string, b: string) => number {
    return (a, b) => {
        const ia = list.indexOf(a)
        const ib = list.indexOf(b)

        if(ia === ib) return 0;
        if(ia === -1) return 100;
        if(ib === -1) return 100;
        return ia - ib;
    }
}

export function isNullOrUndefined(input: any): input is undefined {
    if(input === null || input === undefined) return true;
    return false;
}

export function MarkupEditCell<T = PriceLine2|MiscProduct|QuotePriceLine>(props: {
    anchor: any;
    row: T;
    width: number;
    initialValue: string;
    onCancel(): void;
    onDone(value: T): Promise<any>;
}) {
    const [value, setValue] = useState(props.initialValue);

    return (
        <TableCellTextPopper
            anchor={props.anchor}
            alignRight
            onEnter={() => {
                props.onDone(Object.assign({}, props.row, parseMarkup(value)))
            }}
            onEsc={props.onCancel}
            onTab={props.onCancel}
            onArrow={() => props.onCancel()}
            onBlur={props.onCancel}
            value={value}
            onChange={setValue}
        />
    )
}

export function parseMarkup(value: string) {
    let dollars = () => {
        let val = value.replace("$", "");

        if(val.indexOf(".") === -1) {
            val = val + ".00";
        }

         return {
            markupCents: parseCents(val),
            markupPercent: null,
        }
    }

    let percent = () => {
        console.log("result: percent", value)
        return {
            markupCents: null,
            markupPercent: parseInt(value.replace("%", "")),
        };
    }

    let auto = () => {
        console.log("result: auto", value)
        return {
            markupCents: null,
            markupPercent: null,
        }
    }

    try {

        if (value.startsWith("$")) {
            return dollars();
        } else if (value.endsWith("%")) {
            return percent();
        }

        if (value.indexOf(".") !== -1) {
            return dollars();
        }

        if (value.toLowerCase() === "auto") {
            return auto();
        }

        let v = parseInt(value)
        if (!isNaN(v)) {
            return percent();
        }

        return auto();
    } catch (e) {
        return auto();
    }
}